import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import Popover from "@cloudscape-design/components/popover";
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchNotificationsPaginated, markNotificationRead } from '../api/secureRequests';
import { useAuth } from '../context/AuthContext';
import { useUserRelations } from '../context/UserRelationsContext';

interface Notification {
  id: string;
  type: string;
  title: string;
  preview: string;
  full_message?: string;
  created_at: string;
  read: boolean;
  priority: string;
  actions?: Array<{ label: string; url: string; style: string }>;
  metadata?: Record<string, any>;
}

const NotificationBell: React.FC = () => {
  const { notifications, notificationsLastKey, unreadNotificationCount } = useUserRelations();
  const { idToken } = useAuth();
  const navigate = useNavigate();

  const [allNotifications, setAllNotifications] = useState<Notification[]>(notifications);
  const [currentLastKey, setCurrentLastKey] = useState<string | null>(notificationsLastKey);
  const [loading, setLoading] = useState(false);

  // Update local state when context changes
  useEffect(() => {
    setAllNotifications(notifications);
    setCurrentLastKey(notificationsLastKey);
  }, [notifications, notificationsLastKey]);

  const loadMoreNotifications = async () => {
    if (!currentLastKey || loading || !idToken) return;
    setLoading(true);
    try {
      const data = await fetchNotificationsPaginated(currentLastKey, idToken);
      if (data.notifications) {
        setAllNotifications(prev => [...prev, ...data.notifications]);
        setCurrentLastKey(data.lastKey);
      }
    } catch (error) {
      console.error('Error loading more notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const handleMarkAsRead = async (notification: Notification) => {
    if (!idToken || notification.read) return;

    try {
      await markNotificationRead(notification.id, undefined, idToken);
      setAllNotifications(prev => 
        prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleActionClick = (action: { url: string }, notification: Notification) => {
    handleMarkAsRead(notification);
    navigate(action.url);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'pattern_published':
        return '✅';
      case 'pattern_rejected':
        return '❌';
      case 'review_received':
        return '⭐';
      case 'follow':
        return '👤';
      case 'system':
        return '📢';
      default:
        return '🔔';
    }
  };

  const getTitle = (notification: Notification) => {
    return notification.title || 'Notification';
  };

  const getPreview = (notification: Notification) => {
    return notification.preview || '';
  };

  return (
    <Popover
      position="bottom"
      size="medium"
      triggerType="custom"
      dismissButton={false}
      content={
        <Box>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Box variant="h3" margin={{ bottom: "none" }}>Notifications</Box>
            <Button 
              variant="link" 
              onClick={() => navigate('/notifications')}
              fontSize="12px"
            >
              View all
            </Button>
          </div>
          <div style={{ minWidth: 320, maxHeight: '400px', overflowY: 'auto' }}>
            {allNotifications.length === 0 ? (
              <Box padding="m" color="inherit">
                No notifications yet
              </Box>
            ) : (
              <>
                {allNotifications.slice(0, 5).map((notification) => (
                  <Box
                    key={notification.id}
                    padding="s"
                    margin={{ bottom: "xxs" }}
                    backgroundColor={!notification.read ? "blue-100" : "default"}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleMarkAsRead(notification)}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, maxWidth: '100%' }}>
                      <span style={{ fontSize: 18, flexShrink: 0 }}>
                        {getNotificationIcon(notification.type)}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontWeight: notification.read ? 400 : 600, 
                          wordWrap: 'break-word', 
                          overflowWrap: 'break-word',
                          marginBottom: 2,
                          fontSize: 14
                        }}>
                          {getTitle(notification)}
                        </div>
                        <div style={{ 
                          fontSize: 12, 
                          color: "#666", 
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          marginBottom: 4
                        }}>
                          {getPreview(notification)}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 11, color: "#999" }}>
                            {formatTimeAgo(notification.created_at)}
                          </span>
                          {!notification.read && (
                            <span style={{ color: '#3b82f6', fontSize: 12 }}>•</span>
                          )}
                        </div>
                        {notification.actions && notification.actions.length > 0 && (
                          <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {notification.actions.slice(0, 2).map((action, index) => (
                              <button
                                key={index}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: 11,
                                  border: '1px solid #d1d5db',
                                  borderRadius: 4,
                                  background: action.style === 'primary' ? '#3b82f6' : 'white',
                                  color: action.style === 'primary' ? 'white' : '#374151',
                                  cursor: 'pointer'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleActionClick(action, notification);
                                }}
                              >
                                {action.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </Box>
                ))}
                {allNotifications.length > 5 && (
                  <Box padding="s" textAlign="center">
                    <Button 
                      onClick={() => navigate('/notifications')} 
                      variant="link"
                      fontSize="12px"
                    >
                      View all {allNotifications.length} notifications
                    </Button>
                  </Box>
                )}
                {currentLastKey && allNotifications.length <= 5 && (
                  <Box padding="s">
                    <Button onClick={loadMoreNotifications} loading={loading} variant="link">
                      Load more
                    </Button>
                  </Box>
                )}
              </>
            )}
          </div>
        </Box>
      }
    >
      <div style={{ position: "relative" }}>
        <div style={{
          filter: 'invert(1) brightness(2)',
          display: 'inline-block'
        }}>
          <Button
            iconName="notification"
            variant="icon"
            ariaLabel="Notifications"
          />
        </div>
        {unreadNotificationCount > 0 && (
          <span
            style={{
              position: "absolute",
              top: 2,
              right: 2,
              background: "#ef4444",
              color: "white",
              borderRadius: "50%",
              fontSize: 11,
              width: 18,
              height: 18,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 600,
              zIndex: 2,
              pointerEvents: "none"
            }}
          >
            {unreadNotificationCount > 99 ? "99+" : unreadNotificationCount}
          </span>
        )}
      </div>
    </Popover>
  );
};

export default NotificationBell;
